import React from 'react';
import { useChartColors, money, pctChange } from './theme';

// A single current value belongs in a stat tile, not a one-bar chart. The delta is
// the point: "revenue" alone is a number, "revenue, up 12% on the previous 90 days"
// is information.
function Delta({ current, prior, invert = false }) {
  const pct = pctChange(current, prior);
  if (pct == null) return null;
  const up = pct >= 0;
  const good = invert ? !up : up;
  const tone = good ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400';
  const text = pct === Infinity ? 'new' : `${up ? '+' : ''}${pct.toFixed(0)}%`;
  return (
    <span className={`text-xs font-medium ${tone}`}>
      <span aria-hidden="true">{up ? '▲' : '▼'}</span> {text}
      <span className="sr-only">{up ? 'up' : 'down'} versus the previous period</span>
    </span>
  );
}

function Tile({ label, value, children }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-baseline gap-2">
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
        {children}
      </div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

// A ratio against a limit is a meter, not a two-slice pie. Track and fill are steps
// of one hue so the fill reads as "part of" the whole rather than as a rival series.
function UtilizationTile({ claimed, sold, ratio }) {
  const c = useChartColors();
  const pct = ratio == null ? null : Math.round(ratio * 100);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className="flex items-baseline gap-2">
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {pct == null ? '-' : `${pct}%`}
        </div>
        <span className="text-xs text-gray-500">{claimed} of {sold} seats</span>
      </div>
      <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: c.track }}
           role="meter" aria-valuenow={pct ?? 0} aria-valuemin={0} aria-valuemax={100}
           aria-label="Share of sold seats that have been claimed">
        <div className="h-full rounded-full" style={{ width: `${pct ?? 0}%`, background: c.series }} />
      </div>
      <div className="text-xs text-gray-500 mt-1">Seats claimed</div>
    </div>
  );
}

export default function KpiRow({ analytics }) {
  const prior = analytics?.prior;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
      <Tile label="Seats sold" value={analytics?.seats_sold ?? 0}>
        <Delta current={analytics?.seats_sold ?? 0} prior={prior?.seats_sold} />
      </Tile>
      <Tile label="Orders collected" value={analytics?.by_state?.collected?.count ?? 0}>
        <Delta current={analytics?.by_state?.collected?.count ?? 0} prior={prior?.count} />
      </Tile>
      <Tile label="Average order" value={money(analytics?.aov_cents)}>
        <Delta current={analytics?.aov_cents ?? 0} prior={prior?.aov_cents} />
      </Tile>
      <UtilizationTile claimed={analytics?.seats_claimed ?? 0} sold={analytics?.seats_sold ?? 0}
                       ratio={analytics?.seat_utilization} />
    </div>
  );
}
